import Util from '../../../../../../../misc/util';

export * from './validations';

import { Grid, GridProps } from '@mui/material';
import { useField, useFormikContext } from 'formik';
import { useEffect, useState } from 'react';
import { FormFieldProps } from '..';
import { DatePicker, DatePickerProps } from '../../../../../atomic/input/datePicker';
import { TextField } from '../../../../../atomic/input/textField';
import { FormFieldWrapper } from '../../shared/formFieldWrapper';
import { INVALID_DATE, InvalidDate } from './validations';
import { format } from 'date-fns';

type FormDatePickerProps = FormFieldProps<InvalidDate | string> &
  Omit<DatePickerProps, 'error' | 'helperText' | 'onChange' | 'onChangeInvalidDate'> &
  Pick<GridProps, 'xs' | 'sm' | 'md' | 'lg' | 'xl'>;

export const FormDatePicker = FormFieldWrapper<FormDatePickerProps>((props) => {
  const { setFieldValue, setFieldTouched, values } = useFormikContext();
  const [field, meta] = useField<string>(props.name);
  const [isOpen, setIsOpen] = useState(false);
  const setTouched = (shouldValidate = false) => setFieldTouched(props.name, true, shouldValidate);

  useEffect(() => {
    if (props.initialValue) {
      setFieldValue(props.name, props.initialValue);
    }
  }, []);

  useEffect(() => {
    props.additionalOnChange?.(field.value, setFieldValue);
  }, [field.value]);

  const setValue = (value: InvalidDate | string) => {
    setFieldValue(props.name, value);
  };

  const getDate = () => {
    return values && typeof values === 'object'
      ? (values[props.name as keyof typeof values] as string)
      : undefined;
  };

  const onBlur = () => {
    setTouched(true);

    const date = getDate();
    if (!date) return;
    const parsedYearIsoString = format(Util.parseTwoDigitYearsDate(date), 'yyyy-MM-dd');
    setValue(parsedYearIsoString);
  };

  return (
    <Grid item xs={props.xs ?? 12} sm={props.sm} md={props.md} lg={props.lg} xl={props.xl}>
      <DatePicker
        {...props}
        initialValue={field.value}
        onChangeInvalidDate={(invalidInput) => {
          const newValue = !invalidInput ? '' : INVALID_DATE;
          setValue(newValue);
        }}
        onChange={(isoString) => {
          setValue(isoString);
        }}
        onClear={() => {
          setValue('');
          setTouched(true);
        }}
        open={isOpen}
        onOpen={() => setIsOpen(true)}
        renderInput={(inputRef, inputProps, InputProps): JSX.Element => {
          return (
            <TextField
              missing={props.missing}
              missingMessage={props.missingMessage}
              label={props.label}
              inputProps={{
                ref: inputRef,
                ...inputProps,
                className: 'cursor-pointer',
                readOnly: true,
                placeholder: props.placeholder ?? '',
                onClick: () => {
                  setIsOpen(true);
                }
              }}
              endAdornment={InputProps?.endAdornment}
              error={meta.touched && Boolean(meta.error)}
              helperText={meta.touched && meta.error}
              required={props.required}
              onBlur={onBlur}
              disabled={props.disabled}
              onClick={() => {
                setIsOpen(true);
              }}
            />
          );
        }}
        onClose={() => {
          setTouched();
          setIsOpen(false);
        }}
      />
    </Grid>
  );
});
